import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRupeeSign,
  FaCarAlt,
  FaClock,
  FaPlay,
  FaCheck,
  FaStar,
} from "react-icons/fa";
import DriverLayout from "../../layouts/DriverLayout";
import {
  getCurrentBooking,
  startTrip,
  completeTrip,
  getDriverProfile,
  reset,
} from "../../features/driver/driverSlice";
import RatingModal from "../../components/shared/RatingModal";
import CODPaymentModal from "../../components/shared/CODPaymentModal";
import EarningsBreakdown from "../../components/Driver/EarningsBreakdown";
import socketService from "../../utils/socketService";
import ratingService from "../../services/ratingService";
import axios from "axios";
import { getDriverToken, isDriverAuthenticated, getDriverAuthHeaders } from "../../utils/driverAuthUtils";

const CurrentRide = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { driver, currentBooking, loading, error, success, message } =
    useSelector((state) => state.driver);
  const [locationUpdateInterval, setLocationUpdateInterval] = useState(null);
  const [mapUrl, setMapUrl] = useState("");
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showCODPaymentModal, setShowCODPaymentModal] = useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [completedBooking, setCompletedBooking] = useState(null);
  const [realTimeStatus, setRealTimeStatus] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showViaCities, setShowViaCities] = useState(false);
  const [actualDistanceInput, setActualDistanceInput] = useState("");
  const [waitingTimeInput, setWaitingTimeInput] = useState("0");
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [calculatedFinalAmount, setCalculatedFinalAmount] = useState(0);
  const [calculatedExtraKm, setCalculatedExtraKm] = useState(0);
  const [calculatedExtraKmCharge, setCalculatedExtraKmCharge] = useState(0);
  const [calculatedWaitingTime, setCalculatedWaitingTime] = useState(0);
  const [calculatedWaitingCharge, setCalculatedWaitingCharge] = useState(0);
  const isPaymentInFlight = useRef(false); // Guard against duplicate payment submissions

  // API URL for backend calls
  // Import API URL from config
  const { API_URL } = require("../../config/apiConfig");


  // Location tracking state
  const [isTrackingLocation, setIsTrackingLocation] = useState(false);
  const [trackingError, setTrackingError]           = useState(null);
  const locationWatchId = useRef(null);
  const lastEmittedCoords = useRef(null);

  // Shareable tracking URL
  const trackingUrl = currentBooking
    ? `${window.location.origin}/track/${currentBooking.bookingId || currentBooking._id}`
    : null;

  const [trackingLinkCopied, setTrackingLinkCopied] = useState(false);

  // Retrieve customer phone number from any available nested field
  const customerPhone = currentBooking?.user?.phone || 
                        currentBooking?.passengerDetails?.phone || 
                        currentBooking?.staff?.phone || 
                        currentBooking?.corporateMemberId?.mobileNumber || 
                        currentBooking?.corporateMemberId?.phone || 
                        currentBooking?.phone || 
                        "";

  // Initialize socket + join driver room
  useEffect(() => {
    socketService.connect();
    if (driver && driver._id) {
      console.log('CurrentRide: Joining driver room', driver._id);
      socketService.joinDriverRoom(driver._id);
    }
    return () => {
      if (locationUpdateInterval) clearInterval(locationUpdateInterval);
      // Stop GPS watch on unmount
      if (locationWatchId.current !== null) {
        navigator.geolocation.clearWatch(locationWatchId.current);
        locationWatchId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver?._id]);

  // Ensure current booking loads even after refresh by falling back to localStorage
  useEffect(() => {
    const loadBooking = async () => {
      try {
        let driverId = driver?._id;
        if (!driverId) {
          const driverAuthState = { driver: driverAuth?.driver, token: driverAuth?.token };
          try {
            const { getDriverData } = require('../../utils/driverAuthUtils');
            const storedDriver = getDriverData(driverAuthState);
            driverId = storedDriver?._id;
          } catch {}
        }
        if (driverId) {
          dispatch(getCurrentBooking(driverId));
          dispatch(getDriverProfile(driverId));
        }
      } catch {}
    };
    loadBooking();
  }, [dispatch, driver?._id, driverAuth?.driver?._id]);

  // Set up socket event listeners separately (only once)
  useEffect(() => {
    if (driver && driver._id) {
      // Set up event listeners for socket events
      const bookingStatusListener = (data) => {
        if (
          currentBooking &&
          (data.bookingId === currentBooking.bookingId ||
            data._id === currentBooking._id)
        ) {
          toast.info(`Booking status updated to: ${data.status}`);
          setRealTimeStatus(data.status);
          dispatch(getCurrentBooking(driver._id)); // Refresh booking data
        }
      };

      const rideCompletedListener = (data) => {
        if (
          currentBooking &&
          (data.bookingId === currentBooking.bookingId ||
            data._id === currentBooking._id)
        ) {
          toast.success("Ride completed successfully!");
          setCompletedBooking(data);
          setShowRatingModal(true);
          dispatch(getCurrentBooking(driver._id)); // Refresh booking data
        }
      };

      const rideCancelledListener = (data) => {
        if (
          currentBooking &&
          (data.bookingId === currentBooking.bookingId ||
            data._id === currentBooking._id)
        ) {
          toast.info("This ride has been cancelled by the user");
          dispatch(getCurrentBooking(driver._id)); // Refresh booking data
        }
      };

      // Listen for new ride assignments
      const driverAssignedListener = (assignment) => {
        toast.success("You have been assigned a new ride!");
        dispatch(getCurrentBooking(driver._id)); // Refresh to show new booking
      };

      // Listen for booking unassignment (driver removed)
      const bookingUnassignedListener = (data) => {
        toast.info("You have been removed from this booking");
        dispatch(getCurrentBooking(driver._id)); // Refresh to clear booking
      };

      // Add event listeners and save their cleanup functions
      const cleanStatus = socketService.onBookingStatusChanged(bookingStatusListener);
      const cleanCompleted = socketService.onRideCompleted(rideCompletedListener);
      const cleanCancelled = socketService.onRideCancelled(rideCancelledListener);
      const cleanAssigned = socketService.onDriverAssigned(driverAssignedListener);
      const cleanUnassigned = socketService.onBookingUnassigned(bookingUnassignedListener);

      // Cleanup function to remove the specific listeners we added
      return () => {
        cleanStatus();
        cleanCompleted();
        cleanCancelled();
        cleanAssigned();
        cleanUnassigned();
        dispatch(reset());
      };
    }
  }, [driver?._id, currentBooking, dispatch]);

  // ── GPS live location broadcasting ───────────────────────────────────
  useEffect(() => {
    const rideStatus = realTimeStatus || currentBooking?.status;
    const rideId     = currentBooking?.bookingId || currentBooking?._id;
    // Get passenger's userId so server can route to their user room
    const userId     = currentBooking?.user?._id || currentBooking?.user?.id || currentBooking?.userId;

    const isAssigned    = rideStatus === 'assigned';
    const isInProgress  = rideStatus === 'inProgress';
    const shouldTrack   = (isAssigned || isInProgress) && rideId;

    if (shouldTrack && !isTrackingLocation) {
      if (!navigator.geolocation) {
        setTrackingError('Geolocation not supported on this device.');
        return;
      }

      setIsTrackingLocation(true);
      setTrackingError(null);

      // Join ride room so tracking page gets updates
      socketService.getSocket()?.emit('joinRideRoom', rideId);

      locationWatchId.current = navigator.geolocation.watchPosition(
        (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;

          // Deduplicate: skip if barely moved
          const prev = lastEmittedCoords.current;
          if (prev && Math.abs(prev.lat - lat) < 0.00001 && Math.abs(prev.lng - lng) < 0.00001) {
            return;
          }
          lastEmittedCoords.current = { lat, lng };

          if (isInProgress) {
            // ── Ride in progress: broadcast as locationUpdate (driver marker) ──
            socketService.getSocket()?.emit('locationUpdate', { rideId, lat, lng, type: 'driver' });
            console.log(`[CurrentRide] 📡 Emitted driver locationUpdate for ride ${rideId}`);
          } else if (isAssigned) {
            // ── Driver approaching pickup: broadcast to ride room + passenger room ──
            socketService.getSocket()?.emit('driverApproaching', { rideId, userId, lat, lng });
            console.log(`[CurrentRide] 🚗 Emitted driverApproaching for ride ${rideId} → user ${userId}`);
          }
        },
        (err) => {
          console.warn('[CurrentRide] GPS error:', err.message);
          setTrackingError(`GPS error: ${err.message}`);
        },
        { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
      );

    } else if (!shouldTrack && locationWatchId.current !== null) {
      // Ride ended or cancelled – stop watching
      navigator.geolocation.clearWatch(locationWatchId.current);
      locationWatchId.current = null;
      setIsTrackingLocation(false);
    }

    return () => {};
  }, [realTimeStatus, currentBooking?.status, currentBooking?.bookingId, currentBooking?._id, currentBooking?.user, isTrackingLocation]);
  // ─────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(reset());
    }

    if (success) {
      toast.success(message);
      dispatch(reset());
    }
  }, [error, success, message, dispatch]);

  useEffect(() => {
    if (currentBooking) {
      // Create Google Maps URL for directions
      const pickupAddress = encodeURIComponent(currentBooking.pickupAddress);
      const dropAddress = encodeURIComponent(currentBooking.dropAddress);
      setMapUrl(
        `https://www.google.com/maps/dir/?api=1&origin=${pickupAddress}&destination=${dropAddress}&travelmode=driving`,
      );
    }
  }, [currentBooking]);

  // Handle page refresh/reload when trip is completed but payment or rating is pending
  // NOTE: Only triggers for 'completed' status - NOT 'inProgress'.
  // The inProgress -> payment -> complete flow is handled manually by handleCompleteTrip.
  useEffect(() => {
    const effectiveStatus = realTimeStatus || currentBooking?.status;
    if (currentBooking && effectiveStatus === "completed") {
      const finalAmountToCollect = currentBooking.finalAmount || currentBooking.remainingAmount || currentBooking.totalAmount || 0;

      // Full payment rides: paid at booking time via ONLINE/WALLET/COMPANY (not COD).
      // Using paymentMethod as the source of truth — NOT advancePaid — because
      // Razorpay/online full payments may not set advancePaid at all.
      const remainingAmountRaw = currentBooking.remainingAmount !== undefined && currentBooking.remainingAmount !== null
        ? currentBooking.remainingAmount
        : ((currentBooking.totalAmount || currentBooking.amount || 0) - (currentBooking.advancePaid || 0));
      const isFullyPaidRide = currentBooking.paymentMethod?.toLowerCase() !== 'cod' && 
                              currentBooking.paymentStatus === 'completed' && 
                              remainingAmountRaw <= 0;
      const extrasToCollect = (currentBooking.extraKmCharge || 0) + (currentBooking.waitingCharge || 0);
      // amountActuallyDue: for full-payment rides only extras matter; for COD the full remaining fare
      const amountActuallyDue = isFullyPaidRide ? extrasToCollect : finalAmountToCollect;

      if (!currentBooking.isPaymentCollected && amountActuallyDue > 0 && !isPaymentInFlight.current && !showCODPaymentModal) {
        setCompletedBooking(currentBooking);
        setShowCODPaymentModal(true);
      } else if ((currentBooking.isPaymentCollected || amountActuallyDue <= 0) && !currentBooking.driverRating && !showRatingModal) {
        // Show rating when payment is confirmed OR when there's nothing to collect (full-payment + no extras)
        setCompletedBooking(currentBooking);
        setShowRatingModal(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBooking?.status, currentBooking?.isPaymentCollected, currentBooking?.driverRating, realTimeStatus]);

  const handleStartTrip = () => {

    if (currentBooking && driver) {
      // Check if OTP is required and provided
      if (currentBooking.startOtp && !otpInput) {
        setShowOtpInput(true);
        toast.warn("Please enter the trip start OTP");
        return;
      }

      dispatch(
        startTrip({
          driverId: driver._id,
          bookingId: currentBooking._id,
          otp: otpInput || undefined,
        }),
      ).then((result) => {
        // After trip start, refresh driver profile to ensure dashboard shows correct status
        if (result.type === "driver/startTrip/fulfilled") {
          // Refresh both driver profile and current booking to ensure populated user/passenger details
          dispatch(getDriverProfile(driver._id));
          dispatch(getCurrentBooking(driver._id));
          setOtpInput("");
          setShowOtpInput(false);
        }
      });
    } else {
      // Missing required data
    }
  };

  const handleOpenCompletionModal = () => {
    setShowCompletionModal(true);
  };

  const handleCompleteTripDirectly = () => {
    if (currentBooking && driver) {
      dispatch(
        completeTrip({
          driverId: driver._id,
          bookingId: currentBooking._id,
          actualDistance: actualDistanceInput !== "" ? Number(actualDistanceInput) : 0,
          waitingTime: waitingTimeInput !== "" ? Number(waitingTimeInput) : 0,
        }),
      ).then((result) => {
        if (result.type === "driver/completeTrip/fulfilled") {
          const updatedBooking = result.payload?.booking || result.payload?.data?.booking || currentBooking;
          setCompletedBooking(updatedBooking);
          setShowCompletionModal(false);

          dispatch(getDriverProfile(driver._id));

          setTimeout(() => {
            dispatch(getDriverProfile(driver._id));
          }, 1000);

          // Show rating modal directly
          setTimeout(() => {
            setShowRatingModal(true);
            toast.info("Please rate your passenger to complete the trip");
          }, 500);
        }
      });
    }
  };

  const handleOpenPaymentBeforeCompletion = (calculatedAmount) => {
    setCalculatedFinalAmount(calculatedAmount);
    setShowCompletionModal(false);
    setCompletedBooking(currentBooking);
    setShowCODPaymentModal(true);
  };

  const handleCompleteTrip = () => {
    if (!currentBooking) return;

    const remainingAmountRaw = currentBooking.remainingAmount !== undefined && currentBooking.remainingAmount !== null
      ? currentBooking.remainingAmount
      : ((currentBooking.totalAmount || currentBooking.amount || 0) - (currentBooking.advancePaid || 0));
    const isFullyPaid = currentBooking.paymentMethod?.toLowerCase() !== 'cod' && 
                        currentBooking.paymentStatus === 'completed' && 
                        remainingAmountRaw <= 0;

    const cabTypeObj = currentBooking.cabType;
    const includedKm = cabTypeObj ? (Number(cabTypeObj.includedKm) || 0) : 0;
    const extraFarePerKm = cabTypeObj ? (Number(cabTypeObj.extraFarePerKm) || 0) : 0;
    const freeWaitingMinutes = 0;
    let waitingChargePerMinute = 0;
    if (driver?.vehicleType?.waitingChargePerMinute !== undefined && driver?.vehicleType?.waitingChargePerMinute !== null) {
      waitingChargePerMinute = Number(driver.vehicleType.waitingChargePerMinute);
    } else if (cabTypeObj) {
      waitingChargePerMinute = Number(cabTypeObj.waitingChargePerMinute) || 0;
    }
    const distanceInputVal = actualDistanceInput !== "" ? Number(actualDistanceInput) : 0;
    const waitingInputVal = waitingTimeInput !== "" ? Number(waitingTimeInput) : 0;
    const extraKm = Math.max(0, distanceInputVal - includedKm);
    const extraKmCharge = extraKm * extraFarePerKm;
    const chargeableWaiting = Math.max(0, waitingInputVal - freeWaitingMinutes);
    const waitingCharge = chargeableWaiting * waitingChargePerMinute;

    // Full payment rides: driver only collects extra charges (no base fare)
    // COD rides: driver collects remaining base fare + extras
    const baseDueForCollection = isFullyPaid ? 0 : remainingAmountRaw;
    const liveCalculatedDue = baseDueForCollection + extraKmCharge + waitingCharge;

    setCalculatedExtraKm(extraKm);
    setCalculatedExtraKmCharge(extraKmCharge);
    setCalculatedWaitingTime(chargeableWaiting);
    setCalculatedWaitingCharge(waitingCharge);

    if (currentBooking?.isPaymentCollected || liveCalculatedDue <= 0) {
      handleCompleteTripDirectly();
    } else {
      handleOpenPaymentBeforeCompletion(liveCalculatedDue);
    }
  };

  // Handle COD payment confirmation
  const handleCODPaymentConfirmation = async (paymentData) => {
    // Prevent duplicate submissions via double-click or re-render
    if (isPaymentInFlight.current) {
      console.warn("Payment confirmation already in flight, ignoring duplicate call.");
      return;
    }
    isPaymentInFlight.current = true;
    try {
      setIsSubmittingPayment(true);

      // Use utility function for authentication check
      if (!isDriverAuthenticated(driverAuth)) {
        console.error("Authentication failed - driver not properly authenticated");
        toast.error("You must be logged in as a driver to confirm payment");
        return;
      }

      const token = getDriverToken(driverAuth);

      if (!completedBooking) {
        toast.error("No booking information available");
        return;
      }


      // Debug API URL construction - API_URL already contains /api
      const fullUrl = `${API_URL}/booking/${completedBooking._id}/confirm-payment`;
      const authHeaders = getDriverAuthHeaders(driverAuth);


      // Call API to confirm payment collection
      const response = await axios.put(
        fullUrl,
        {
          paymentMethod: paymentData.method,
          amount: paymentData.amount,
          isPaymentCollected: true,
          walletOtp: paymentData.walletOtp, // Pass the passenger's wallet OTP
        },
        {
          headers: authHeaders,
        },
      );

      if (response.data.success) {
        toast.success("Payment confirmed successfully!");
        setShowCODPaymentModal(false);

        // Now complete the trip on backend
        toast.info("Completing trip on backend...");
        dispatch(
          completeTrip({
            driverId: driver._id,
            bookingId: currentBooking._id,
            actualDistance: actualDistanceInput !== "" ? Number(actualDistanceInput) : 0,
            waitingTime: waitingTimeInput !== "" ? Number(waitingTimeInput) : 0,
          })
        ).then((result) => {
          if (result.type === "driver/completeTrip/fulfilled") {
            const updatedBooking = result.payload?.booking || result.payload?.data?.booking || currentBooking;
            setCompletedBooking(updatedBooking);

            dispatch(getDriverProfile(driver._id));
            setTimeout(() => {
              dispatch(getDriverProfile(driver._id));
            }, 1000);

            // Now show rating modal
            setTimeout(() => {
              setShowRatingModal(true);
              toast.info("Please rate your passenger to complete the trip");
            }, 500);
          } else {
            toast.error(result.payload || "Failed to complete trip after payment");
          }
        });
      } else {
        toast.error(response.data.message || "Failed to confirm payment");
        isPaymentInFlight.current = false; // Allow retry on server-side rejection
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
      toast.error(error.response?.data?.message || "Failed to confirm payment");
      isPaymentInFlight.current = false; // Allow retry on error
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  // Handle submission of passenger rating
  const handleSubmitRating = async (ratingData) => {
    try {
      setIsSubmittingRating(true);

      // Use utility function for authentication check
      if (!isDriverAuthenticated(driverAuth)) {
        console.error("Authentication failed - driver not properly authenticated for rating");
        toast.error("You must be logged in as a driver to submit a rating");
        return;
      }

      const authHeaders = getDriverAuthHeaders(driverAuth);

      if (!authHeaders) {
        toast.error("Authentication failed - please log in again");
        return;
      }

      if (!completedBooking) {
        toast.error("No booking information available");
        return;
      }

      // Check if passenger info exists - either from user field or from corporate staff
      const hasPassengerInfo =
        completedBooking.user ||
        completedBooking.corporateMemberId ||
        completedBooking.passengerDetails?.name;

      if (!hasPassengerInfo) {
        toast.error("No passenger information available to rate");
        return;
      }

      // Get user ID - handling both regular users and corporate staff
      // For regular users, use the user field
      // For corporate staff, use the corporateMemberId
      let userId;
      let bookingType = "user"; // Default type

      if (completedBooking.user) {
        userId = typeof completedBooking.user === "object"
          ? completedBooking.user._id
          : completedBooking.user;
        bookingType = "user";
      } else if (completedBooking.corporateMemberId) {
        userId = typeof completedBooking.corporateMemberId === "object"
          ? completedBooking.corporateMemberId._id
          : completedBooking.corporateMemberId;
        bookingType = "corporate_staff";
      } else if (completedBooking.passengerDetails?.name) {
        // Fallback for bookings with only passengerDetails
        // Try to use passengerDetails phone or email as identifier
        userId = completedBooking._id; // Use booking ID as fallback
        bookingType = "unknown";
      }

      if (!userId) {
        toast.error("Cannot identify passenger for rating");
        return;
      }


      // Use the rating service to submit the driver's rating for the user
      const response = await ratingService.submitDriverRating(
        completedBooking._id,
        userId,
        ratingData.rating,
        ratingData.comment,
        authHeaders,
      );

      if (response.success) {
        toast.success("Thank you for rating your passenger!");
        setShowRatingModal(false);
        // Refresh current booking to update UI
        dispatch(getCurrentBooking(driver._id));
        // Redirect to ride history
        navigate("/driver/ride-history");
      } else {
        toast.error(response.message || "Failed to submit rating");
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      toast.error(error.response?.data?.message || "Failed to submit rating");
    } finally {
      setIsSubmittingRating(false);
    }
  };

  if (loading && !currentBooking && !driver) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-500"></div>
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-black text-slate-800">Current Assigned Trip</h1>
          {currentBooking && (
            <span className="text-xs font-bold text-slate-450 uppercase tracking-wider">
              ID: {currentBooking.bookingId}
            </span>
          )}
        </div>

        {currentBooking ? (
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
            {/* Gradient Header */}
            <div className="bg-slate-900 text-white px-6 py-4 border-b border-slate-800">
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 font-extrabold text-sm">
                    🚕
                  </div>
                  <div>
                    <h2 className="text-sm font-black tracking-tight text-slate-100">
                      Active Booking #{currentBooking.bookingId}
                    </h2>
                    <p className="text-[10px] text-slate-400 font-semibold mt-0.5">
                      Assigned to you
                    </p>
                  </div>
                </div>
                
                <span
                  className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${(realTimeStatus || currentBooking.status) === "assigned"
                    ? "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                    : (realTimeStatus || currentBooking.status) === "inProgress"
                      ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                      : (realTimeStatus || currentBooking.status) === "completed"
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        : "bg-slate-800 text-slate-400"
                    }`}
                >
                  {(realTimeStatus || currentBooking.status) === "assigned"
                    ? "Approaching Pickup"
                    : (realTimeStatus || currentBooking.status) === "inProgress"
                      ? "Trip In Progress"
                      : (realTimeStatus || currentBooking.status) === "completed"
                        ? "Completed"
                        : currentBooking.status}
                </span>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Columns - Details */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Service & Payment Overview */}
                  <div className="bg-slate-50 border border-slate-100 rounded-3xl p-5 grid grid-cols-1 md:grid-cols-3 gap-6 shadow-sm">
                    {/* Service & Package Details */}
                    <div>
                      <h4 className="text-xs font-black text-slate-450 uppercase tracking-widest mb-3">Service & Package Details</h4>
                      <div className="flex flex-col gap-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-100 rounded-xl text-xs font-bold text-purple-700 w-fit">
                          🚗 {currentBooking.cabType?.name || "Standard Class"}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-xl text-xs font-bold text-blue-700 w-fit">
                          ➡️ {currentBooking.journeyType === "oneWay" ? "One Way" :
                              currentBooking.journeyType === "roundTrip" ? "Round Trip" :
                              currentBooking.journeyType === "rental" ? "Hourly Rental" :
                              currentBooking.journeyType || "One Way"}
                        </span>
                      </div>
                    </div>

                    {/* Amount & Payment */}
                    <div>
                      <h4 className="text-xs font-black text-slate-450 uppercase tracking-widest mb-3">Amount & Payment</h4>
                      <div className="space-y-2">
                        <div className="font-extrabold text-slate-900 text-lg">
                          ₹{currentBooking.totalAmount || currentBooking.amount || 0}
                        </div>
                        {currentBooking.advancePaid > 0 ? (
                          <div className="space-y-1.5 bg-amber-500/5 border border-amber-500/10 p-2.5 rounded-2xl">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black bg-amber-100 text-amber-800 border border-amber-250">
                              ⚡ Advance Ride
                            </span>
                            <div className="text-[11px] text-slate-600 font-bold flex justify-between">
                              <span>Paid:</span>
                              <span className="text-emerald-600">₹{currentBooking.advancePaid}</span>
                            </div>
                            <div className="text-[11px] text-slate-600 font-bold flex justify-between">
                              <span>Due:</span>
                              <span className="text-rose-600">
                                ₹{currentBooking.remainingAmount || ((currentBooking.totalAmount || currentBooking.amount || 0) - currentBooking.advancePaid)}
                              </span>
                            </div>
                          </div>
                        ) : null}
                        <div>
                          {/* Payment status badge — distinguishes Full Payment from COD */}
                          {(() => {
                            const remainingAmountRaw = currentBooking.remainingAmount !== undefined && currentBooking.remainingAmount !== null
                              ? currentBooking.remainingAmount
                              : ((currentBooking.totalAmount || currentBooking.amount || 0) - (currentBooking.advancePaid || 0));
                            const isFullyPaid = currentBooking.paymentMethod?.toLowerCase() !== 'cod' && 
                                                currentBooking.paymentStatus === 'completed' && 
                                                remainingAmountRaw <= 0;
                            return (
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[10px] font-black border ${
                                isFullyPaid
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-blue-50 text-blue-750 border-blue-200"
                              }`}>
                                {currentBooking.paymentMethod?.toUpperCase() === "COD"
                                  ? "💵 Cash / COD"
                                  : isFullyPaid
                                    ? currentBooking.paymentMethod?.toUpperCase() === "WALLET"
                                      ? "✅ Full Payment (Paid via Wallet)"
                                      : "✅ Full Payment (Paid Online)"
                                    : currentBooking.advancePaid > 0
                                      ? "🔐 Advance Paid"
                                      : "🔐 Online"}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Status</h4>
                      <span
                        className={`inline-block px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider ${(realTimeStatus || currentBooking.status) === "assigned"
                          ? "bg-amber-100 text-amber-800 border border-amber-200"
                          : (realTimeStatus || currentBooking.status) === "inProgress"
                            ? "bg-blue-100 text-blue-800 border border-blue-200"
                            : (realTimeStatus || currentBooking.status) === "completed"
                              ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                              : "bg-slate-100 text-slate-700 border border-slate-200"
                          }`}
                      >
                        {(realTimeStatus || currentBooking.status) === "assigned"
                          ? "Approaching Pickup"
                          : (realTimeStatus || currentBooking.status) === "inProgress"
                            ? "Trip In Progress"
                            : (realTimeStatus || currentBooking.status) === "completed"
                              ? "Completed"
                              : currentBooking.status}
                      </span>
                    </div>
                  </div>

                  {/* Visual Route Timeline */}
                  <div>
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Route Timeline</h3>
                    <div className="relative pl-8 space-y-6 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200/80">
                      {/* Pickup */}
                      <div className="relative">
                        <span className="absolute -left-8 mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-bold text-emerald-500">
                          🟢
                        </span>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pickup Address</span>
                          <h4 className="text-sm font-extrabold text-slate-800 mt-0.5">
                            {currentBooking.pickupLocation?.name || currentBooking.from || "Point"}
                          </h4>
                          <p className="text-xs text-slate-500 mt-1">{currentBooking.pickupAddress}</p>
                        </div>
                      </div>

                      {/* Via Cities Dropdown / Interactive List */}
                      {currentBooking.viaCities && currentBooking.viaCities.length > 0 && (
                        <div className="relative bg-slate-50 border border-slate-100 rounded-2xl p-4">
                          <button
                            onClick={() => setShowViaCities(!showViaCities)}
                            className="flex items-center justify-between w-full text-left focus:outline-none"
                          >
                            <span className="text-xs font-bold text-indigo-600 flex items-center gap-1.5">
                              📍 Via Cities / Stopovers ({currentBooking.viaCities.length})
                            </span>
                            <span className="text-xs font-bold text-slate-450 hover:text-slate-600 transition-colors">
                              {showViaCities ? 'Hide stops ▴' : 'View stops ▾'}
                            </span>
                          </button>
                          
                          {showViaCities && (
                            <div className="mt-3 pt-3 border-t border-slate-200/50 space-y-2 animate-fadeIn">
                              {currentBooking.viaCities.map((city, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs font-semibold text-slate-650">
                                  <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                                  <span>{city}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Drop */}
                      <div className="relative">
                        <span className="absolute -left-8 mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-rose-50 border border-rose-200 text-[10px] font-bold text-rose-500">
                          🔴
                        </span>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Drop Address</span>
                          <h4 className="text-sm font-extrabold text-slate-800 mt-0.5">
                            {currentBooking.dropLocation?.name || currentBooking.to || "Destination"}
                          </h4>
                          <p className="text-xs text-slate-500 mt-1">{currentBooking.dropAddress}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Booking Metadata Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6 border-t border-slate-100">
                    <div>
                      <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Service Parameters</h4>
                      <div className="space-y-2 text-xs text-slate-600">
                        <p className="flex justify-between py-1 border-b border-slate-50">
                          <span className="text-slate-450">Service Type</span>
                          <span className="font-bold capitalize text-slate-800">
                            {currentBooking.serviceType === "rental" ? "Local Rental" :
                              currentBooking.serviceType === "airport" ? "Airport Transfer" :
                                currentBooking.serviceType || "Outstation"}
                          </span>
                        </p>
                        <p className="flex justify-between py-1 border-b border-slate-50">
                          <span className="text-slate-450">Cab Class</span>
                          <span className="font-bold text-slate-800">{currentBooking.cabType?.name || "Standard"}</span>
                        </p>
                        {currentBooking.serviceType === "rental" && (
                          <>
                            {currentBooking.packageName && (
                              <p className="flex justify-between py-1 border-b border-slate-50">
                                <span className="text-slate-450">Package</span>
                                <span className="font-bold text-slate-800">{currentBooking.packageName}</span>
                              </p>
                            )}
                            {currentBooking.rentalHours && (
                              <p className="flex justify-between py-1 border-b border-slate-50">
                                <span className="text-slate-450">Duration Limit</span>
                                <span className="font-bold text-slate-800">{currentBooking.rentalHours} hours</span>
                              </p>
                            )}
                          </>
                        )}
                        {currentBooking.serviceType === "airport" && (
                          <>
                            {currentBooking.airportCode && (
                              <p className="flex justify-between py-1 border-b border-slate-50">
                                <span className="text-slate-450">Airport</span>
                                <span className="font-bold text-slate-800">{currentBooking.airportCode}</span>
                              </p>
                            )}
                            {currentBooking.transferDirection && (
                              <p className="flex justify-between py-1 border-b border-slate-50">
                                <span className="text-slate-450">Direction</span>
                                <span className="font-bold text-slate-800 capitalize">
                                  {currentBooking.transferDirection === "to" ? "To Airport" : "From Airport"}
                                </span>
                              </p>
                            )}
                          </>
                        )}
                        {currentBooking.selectedAddOns && currentBooking.selectedAddOns.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                              <span>📦</span> Add-on Services (Optional)
                            </p>
                            {currentBooking.selectedAddOns.map((addon, index) => (
                              <p key={index} className="flex justify-between py-0.5 text-xs">
                                <span className="text-slate-450">{addon.name}</span>
                                <span className="font-bold text-slate-800">₹{addon.price}</span>
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Schedule & Distance</h4>
                      <div className="space-y-2 text-xs text-slate-600">
                        <p className="flex justify-between py-1 border-b border-slate-50">
                          <span className="text-slate-450">Pickup Time</span>
                          <span className="font-bold text-slate-800">
                            {currentBooking.pickupDate ? new Date(currentBooking.pickupDate).toLocaleDateString() : ""} {currentBooking.pickupTime ? `at ${currentBooking.pickupTime}` : ""}
                          </span>
                        </p>
                        {(currentBooking.journeyType === "roundTrip" || currentBooking.tripType === "roundTrip" || currentBooking.returnDate) && currentBooking.returnDate && (
                          <p className="flex justify-between py-1 border-b border-slate-50">
                            <span className="text-slate-450">Return Date & Time</span>
                            <span className="font-bold text-indigo-700">
                              {new Date(currentBooking.returnDate).toLocaleDateString()} {currentBooking.returnTime ? `at ${currentBooking.returnTime}` : ""}
                            </span>
                          </p>
                        )}
                        {currentBooking.distance && (
                          <p className="flex justify-between py-1">
                            <span className="text-slate-450">Approx Distance</span>
                            <span className="font-bold text-slate-800">{currentBooking.distance} km</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Trip Fare & Payments */}
                  <div className="pt-6 border-t border-slate-100">
                    <h3 className="text-xs font-black text-slate-450 uppercase tracking-widest mb-4">Trip Fare Breakdown</h3>
                    {currentBooking.totalAmount && currentBooking.totalAmount > 0 ? (
                      <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-4 space-y-4">
                        <EarningsBreakdown booking={currentBooking} />
                        <div className="mt-4 pt-3 border-t border-slate-200/50 space-y-3">
                          <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
                            <span>Payment Mode:</span>
                            <span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-wider">
                              {currentBooking.paymentMethod === "COD" ? "Cash Collect (COD)" : "Prepaid Online"}
                            </span>
                          </div>
                          
                          {currentBooking.advancePaid > 0 && (
                            <div className="bg-amber-500/5 border border-amber-500/10 p-3 rounded-2xl space-y-2 mt-2">
                              <div className="flex justify-between items-center">
                                <span className="text-[10px] px-2 py-0.5 rounded font-black bg-amber-100 text-amber-800 border border-amber-250">
                                  ⚡ Advance Ride
                                </span>
                                <span className={`text-[10px] px-2.5 py-0.5 rounded font-black border ${
                                  currentBooking.paymentMethod?.toUpperCase() === "COD"
                                    ? "bg-green-50 text-green-700 border-green-200"
                                    : "bg-blue-50 text-blue-750 border-blue-200"
                                }`}>
                                  {currentBooking.paymentMethod?.toUpperCase() === "COD" ? "💵 COD" : "🔐 Online"}
                                </span>
                              </div>
                              <div className="flex justify-between items-center text-xs font-bold text-slate-650">
                                <span>Paid:</span>
                                <span className="text-emerald-600 font-extrabold">₹{currentBooking.advancePaid}</span>
                              </div>
                              <div className="flex justify-between items-center text-xs font-bold text-slate-650">
                                <span>Due:</span>
                                <span className="text-rose-600 font-extrabold">
                                  ₹{currentBooking.remainingAmount || (currentBooking.totalAmount - currentBooking.advancePaid)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-400 text-xs italic">Fare details not loaded</p>
                    )}
                  </div>
                </div>

                {/* Right Column - Navigation & Customer */}
                <div className="space-y-6">
                  {/* Passenger Panel */}
                  <div className="bg-slate-50 border border-slate-100 rounded-3xl p-5">
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Passenger Profile</h4>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center font-black text-lg">
                        {(() => {
                          const name = currentBooking.user?.name || currentBooking.passengerDetails?.name || currentBooking.staff?.name || "Passenger";
                          return name.charAt(0).toUpperCase();
                        })()}
                      </div>
                      <div>
                        <h4 className="text-sm font-extrabold text-slate-800">
                          {currentBooking.user?.name || currentBooking.passengerDetails?.name || currentBooking.staff?.name || "Passenger"}
                        </h4>
                        {customerPhone && (
                          <p className="text-xs text-slate-500 font-bold mt-0.5">
                            📱 {customerPhone}
                          </p>
                        )}
                        <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider mt-0.5">
                          {currentBooking.journeyType === "rental" ? "Hourly Rental Customer" : "Standard Passenger"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 pt-4 border-t border-slate-200/50">
                      <a
                        href={`tel:${customerPhone}`}
                        className="flex items-center justify-center gap-2 w-full py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-xl transition duration-200 shadow-sm"
                      >
                        📞 Call Customer {customerPhone && `(${customerPhone})`}
                      </a>
                    </div>
                  </div>

                  {/* Share Tracking Link */}
                  {trackingUrl && (
                    <div className="bg-indigo-50/50 border border-indigo-100 rounded-3xl p-5 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black text-indigo-700 uppercase tracking-widest">Share Live Gps</span>
                        {isTrackingLocation && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-md">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            GPS Live
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Passengers can track your location live in their browsers via this link:
                      </p>
                      <div className="bg-white border border-slate-100 px-3 py-2 rounded-xl text-xs text-slate-650 truncate select-all">
                        {trackingUrl}
                      </div>
                      {trackingError && <p className="text-[10px] text-rose-500">{trackingError}</p>}
                      
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(trackingUrl);
                          setTrackingLinkCopied(true);
                          setTimeout(() => setTrackingLinkCopied(false), 2000);
                        }}
                        className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-750 text-white font-semibold text-xs rounded-xl transition shadow-sm"
                      >
                        {trackingLinkCopied ? '✅ Link Copied!' : '📋 Copy Tracking URL'}
                      </button>
                    </div>
                  )}

                  {/* Map Button */}
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-xl flex items-center justify-center gap-2 shadow-md shadow-blue-500/10 transition"
                  >
                    🗺️ Launch Navigation Map
                  </a>

                  {/* Trip Control Actions */}
                  <div className="pt-4 border-t border-slate-100">
                    {(realTimeStatus || currentBooking.status) === "assigned" && (
                      <div className="space-y-4">
                        {/* OTP input */}
                        {(showOtpInput || currentBooking.startOtp) && (
                          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
                            <label className="block text-xs font-black text-slate-450 uppercase tracking-widest mb-2">
                              Trip Start OTP
                            </label>
                            <input
                              type="text"
                              value={otpInput}
                              onChange={(e) => setOtpInput(e.target.value)}
                              placeholder="Enter 4-digit OTP"
                              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-center font-bold tracking-widest text-sm focus:border-transparent"
                              maxLength="4"
                            />
                            <p className="text-[10px] text-slate-400 mt-2 text-center leading-relaxed">
                              Ask passenger for the OTP sent to their mobile device.
                            </p>
                          </div>
                        )}

                        <button
                          onClick={handleStartTrip}
                          className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-extrabold text-sm rounded-xl flex items-center justify-center gap-2 transition btn-animated"
                        >
                          Start Journey
                        </button>
                      </div>
                    )}

                    {(realTimeStatus || currentBooking.status) === "inProgress" && (
                      <button
                        onClick={handleOpenCompletionModal}
                        className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-sm rounded-xl flex items-center justify-center gap-2 transition btn-animated"
                      >
                        Complete Journey
                      </button>
                    )}

                    {(() => {
                      // Full payment rides: paid at booking time via ONLINE/WALLET/COMPANY (not COD)
                      const remainingAmountRaw = currentBooking.remainingAmount !== undefined && currentBooking.remainingAmount !== null
                        ? currentBooking.remainingAmount
                        : ((currentBooking.totalAmount || currentBooking.amount || 0) - (currentBooking.advancePaid || 0));
                      const fullPaid = currentBooking.paymentMethod?.toLowerCase() !== 'cod' && 
                                       currentBooking.paymentStatus === 'completed' && 
                                       remainingAmountRaw <= 0;
                      const extrasAmt = (currentBooking.extraKmCharge || 0) + (currentBooking.waitingCharge || 0);
                      const finalAmt = currentBooking.finalAmount || currentBooking.remainingAmount || currentBooking.totalAmount || 0;
                      const amtDue = fullPaid ? extrasAmt : finalAmt;
                      const isCompleted = (realTimeStatus || currentBooking.status) === "completed";

                      if (!isCompleted) return null;

                      if (!currentBooking.isPaymentCollected && amtDue > 0) {
                        // Still need to collect extras (or full COD fare)
                        return (
                          <button
                            onClick={() => {
                              setCompletedBooking(currentBooking);
                              setShowCODPaymentModal(true);
                            }}
                            className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-750 text-white font-extrabold text-sm rounded-xl flex items-center justify-center gap-2 transition btn-animated"
                          >
                            {fullPaid ? "Collect Extra Charges" : "Collect Payment"}
                          </button>
                        );
                      }

                      if ((currentBooking.isPaymentCollected || amtDue <= 0) && !currentBooking.driverRating) {
                        // Payment done (or nothing to collect for full-payment + no extras) → show rating
                        return (
                          <button
                            onClick={() => {
                              setCompletedBooking(currentBooking);
                              setShowRatingModal(true);
                            }}
                            className="w-full py-3.5 bg-amber-550 hover:bg-amber-600 text-white font-extrabold text-sm rounded-xl flex items-center justify-center gap-2 transition btn-animated"
                          >
                            Rate Passenger
                          </button>
                        );
                      }

                      return null;
                    })()}
                  </div>
                </div>
              </div>

              {currentBooking.additionalNotes && (
                <div className="mt-8 p-4 bg-amber-50/50 border border-amber-100 rounded-2xl flex gap-3">
                  <span className="text-base">📝</span>
                  <div>
                    <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider">Driver Instructions / Notes</h4>
                    <p className="text-xs text-amber-700 mt-1 leading-relaxed">{currentBooking.additionalNotes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center max-w-xl mx-auto shadow-sm">
            <div className="w-20 h-20 mx-auto bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mb-6 text-4xl">
              📭
            </div>
            <h2 className="text-xl font-black text-slate-800 mb-2">No Active Assigned Rides</h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              You are currently not on any active trip. Make sure your status toggle is active to receive assignment alerts.
            </p>
            <button
              onClick={() => navigate("/driver/dashboard")}
              className="px-6 py-2.5 bg-slate-900 text-white font-semibold text-xs rounded-xl hover:bg-slate-850 transition"
            >
              Return to Dashboard
            </button>
          </div>
        )}

        {/* COD Payment Modal */}
        {completedBooking?._id && (
          <CODPaymentModal
            isOpen={showCODPaymentModal}
            onClose={() => setShowCODPaymentModal(false)}
            onConfirm={handleCODPaymentConfirmation}
            bookingDetails={completedBooking}
            totalAmount={
              calculatedFinalAmount ||
              (completedBooking.advancePaid > 0
                ? (completedBooking.finalAmount || completedBooking.remainingAmount || (completedBooking.totalAmount - completedBooking.advancePaid))
                : (completedBooking.finalAmount || completedBooking.totalAmount))
            }
            isSubmitting={isSubmittingPayment}
            authHeaders={getDriverAuthHeaders(driverAuth)}
            extraKm={calculatedExtraKm}
            extraKmCharge={calculatedExtraKmCharge}
            waitingTime={calculatedWaitingTime}
            waitingCharge={calculatedWaitingCharge}
          />
        )}

        {/* Rating Modal */}
        {completedBooking?._id && (
          <RatingModal
            isOpen={showRatingModal}
            onClose={() => setShowRatingModal(false)}
            onSubmit={handleSubmitRating}
            recipientName={
              completedBooking?.user?.name ||
              completedBooking?.passengerDetails?.name ||
              "Passenger"
            }
            recipientType="user"
            bookingId={completedBooking._id}
          />
        )}

        {/* Complete Journey Modal */}
        {showCompletionModal && currentBooking && (() => {
          const remainingAmount = currentBooking.remainingAmount || ((currentBooking.totalAmount || currentBooking.amount || 0) - (currentBooking.advancePaid || 0));
          const cabTypeObj = currentBooking.cabType;
          const includedKm = cabTypeObj ? (Number(cabTypeObj.includedKm) || 0) : 0;
          const extraFarePerKm = cabTypeObj ? (Number(cabTypeObj.extraFarePerKm) || 0) : 0;
          const freeWaitingMinutes = 0; // No free waiting minutes policy anymore
          
          let waitingChargePerMinute = 0;
          if (driver?.vehicleType?.waitingChargePerMinute !== undefined && driver?.vehicleType?.waitingChargePerMinute !== null) {
            waitingChargePerMinute = Number(driver.vehicleType.waitingChargePerMinute);
          } else if (cabTypeObj) {
            waitingChargePerMinute = Number(cabTypeObj.waitingChargePerMinute) || 0;
          }
          const distanceInputVal = actualDistanceInput !== "" ? Number(actualDistanceInput) : 0;
          const waitingInputVal = waitingTimeInput !== "" ? Number(waitingTimeInput) : 0;
          const extraKm = Math.max(0, distanceInputVal - includedKm);
          const extraKmCharge = extraKm * extraFarePerKm;
          const chargeableWaiting = Math.max(0, waitingInputVal - freeWaitingMinutes);
          const waitingCharge = chargeableWaiting * waitingChargePerMinute;
          const liveCalculatedDue = remainingAmount + extraKmCharge + waitingCharge;

          // Full payment rides: paid at booking time via ONLINE/WALLET/COMPANY (not COD)
          const remainingAmountRaw = currentBooking.remainingAmount !== undefined && currentBooking.remainingAmount !== null
            ? currentBooking.remainingAmount
            : ((currentBooking.totalAmount || currentBooking.amount || 0) - (currentBooking.advancePaid || 0));
          const isFullyPaid = currentBooking.paymentMethod?.toLowerCase() !== 'cod' && 
                              currentBooking.paymentStatus === 'completed' && 
                              remainingAmountRaw <= 0;
          // For full-payment rides: only collect extras; for COD: collect remaining + extras
          const effectiveDue = isFullyPaid ? (extraKmCharge + waitingCharge) : liveCalculatedDue;

          if (typeof document === 'undefined') return null;

          return createPortal(
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-slate-100 p-6 space-y-6">
                <div className="flex justify-between items-center border-b border-slate-100 pb-4">
                  <h3 className="text-base font-black text-slate-800">
                    Complete Journey & Calculate Due
                  </h3>
                  <button
                    onClick={() => setShowCompletionModal(false)}
                    className="text-slate-400 hover:text-slate-655 font-bold"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-4 text-left">
                  <div className="space-y-1">
                    <label className="block text-xs font-black text-slate-450 uppercase tracking-widest">
                      Actual Ride Distance / Extra KM (KM)
                    </label>
                    <input
                      type="number"
                      value={actualDistanceInput}
                      onChange={(e) => setActualDistanceInput(e.target.value)}
                      placeholder={`Enter total KM (Estimated: ${currentBooking.distance} km)`}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold text-sm"
                      min="0"
                      step="0.1"
                    />
                    {extraKm > 0 ? (
                      <p className="text-[10px] text-orange-600 font-bold">
                        ➕ {extraKm.toFixed(1)} Extra KM: +₹{extraKmCharge.toFixed(2)} (Rate: ₹{extraFarePerKm}/km after {includedKm}km)
                      </p>
                    ) : (
                      <p className="text-[10px] text-slate-400">
                        Included distance limit: {includedKm} km (Rate after limit: ₹{extraFarePerKm}/km)
                      </p>
                    )}
                  </div>

                  {true && (
                    <div className="space-y-1">
                      <label className="block text-xs font-black text-slate-450 uppercase tracking-widest">
                        Total Waiting Time (Minutes)
                      </label>
                      <input
                        type="number"
                        value={waitingTimeInput}
                        onChange={(e) => setWaitingTimeInput(e.target.value)}
                        placeholder="Enter waiting time in minutes"
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 font-bold text-sm"
                        min="0"
                      />
                      {waitingInputVal > 0 ? (
                        <p className="text-[10px] text-orange-600 font-bold">
                          ➕ {waitingInputVal} min Billable Wait: +₹{waitingCharge.toFixed(2)} (Formula: {waitingInputVal}m × ₹{waitingChargePerMinute}/min)
                        </p>
                      ) : (
                        <p className="text-[10px] text-slate-400">
                          Info: Waiting Charge Rate: ₹{waitingChargePerMinute}/min (e.g. 10 mins wait = ₹{(10 * waitingChargePerMinute).toFixed(2)})
                        </p>
                      )}
                    </div>
                  )}

                  {/* Live Fare breakdown inside modal */}
                  <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl space-y-2 text-xs">
                    <h5 className="font-black text-[10px] text-slate-400 uppercase tracking-widest pb-1 border-b border-slate-200/50">
                      Calculated Fare Summary
                    </h5>
                    {isFullyPaid ? (
                      <div className="flex justify-between text-emerald-700 font-semibold mt-1 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
                        <span>
                          {currentBooking.paymentMethod?.toUpperCase() === "WALLET"
                            ? "✅ Full Payment (Paid via Wallet)"
                            : "✅ Full Payment (Paid Online)"}
                        </span>
                        <span>₹{(currentBooking.advancePaid || 0).toFixed(2)}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between text-slate-650 font-semibold mt-1">
                        <span>Base Remaining Due:</span>
                        <span>₹{remainingAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {extraKmCharge > 0 && (
                      <div className="flex justify-between text-slate-650 font-semibold">
                        <span>Extra Distance Charge:</span>
                        <span className="text-orange-600">+₹{extraKmCharge.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-slate-650 font-semibold">
                      <span>Waiting Time Charge:</span>
                      <span className="text-orange-600">+₹{waitingCharge.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-black text-slate-800 border-t border-slate-200/70 pt-2 mt-2">
                      <span>{isFullyPaid ? "Extra Charges to Collect:" : "Total Due to Collect:"}</span>
                      <span className="text-emerald-600 text-base font-extrabold">₹{effectiveDue.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowCompletionModal(false)}
                    className="flex-1 py-3 border border-slate-200 text-slate-700 hover:bg-slate-50 font-bold text-xs rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCompleteTrip}
                    className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-xs rounded-xl shadow-sm transition"
                  >
                    Confirm & Complete
                  </button>
                </div>
              </div>
            </div>,
            document.body
          );
        })()}
      </div>
    </DriverLayout>
  );
};

export default CurrentRide;
